import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/lib/contexts/auth-context';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Field, FieldLabel } from '@/components/ui/field';
import { Building2, Camera, Crown, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const FORM_COMPANY_EDIT_ID = 'form-company-edit';

export default function FormCompanyEdit({ company, companyLogoUrl, onSubmit }) {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);
    const { user } = useAuth();
    const [logoPreview, setLogoPreview] = useState(null);
    const [logoFile, setLogoFile] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        siret: '',
        vat: '',
        description: '',
        notes: '',
        street: '',
        postalCode: '',
        city: '',
        email: '',
        billingEmail: '',
        phone: '',
        ...(user.role === 'admin' && { billingIdentifier: '' }),
    });

    useEffect(() => {
        if (company) {
            setFormData({
                name: company.name || '',
                siret: company.siret || '',
                vat: company.vat || '',
                description: company.description || '',
                notes: company.notes || '',
                street: company.street || '',
                postalCode: company.cp || '',
                city: company.city || '',
                email: company.email || '',
                billingEmail: company.billing_email || '',
                phone: company.phone || '',
                ...(user.role === 'admin' && { billingIdentifier: company.billing_id || '' }),
            });
        }
    }, [company]);

    useEffect(() => {
        if (companyLogoUrl) {
            setLogoPreview(companyLogoUrl);
        }
    }, [companyLogoUrl]);

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleLogoSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
            return;
        }
        // Validate file size (2MB max)
        if (file.size > 2 * 1024 * 1024) {
            return;
        }

        setLogoFile(file);
        const reader = new FileReader();
        reader.onload = (ev) => {
            setLogoPreview(ev.target.result);
        };
        reader.readAsDataURL(file);
    };

    const handleLogoRemove = () => {
        setLogoFile(null);
        setLogoPreview(companyLogoUrl || null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.name.trim()) return;
        const address =
            formData.postalCode && formData.city
                ? `${formData.street}, ${formData.postalCode} ${formData.city}`
                : formData.street;

        const submitData = {
            ...company,
            ...formData,
            address,
        };

        // Attach logo data if a new file was selected
        if (logoFile) {
            const reader = new FileReader();
            reader.onload = (ev) => {
                // Extract base64 without the data:image/...;base64, prefix
                const base64 = ev.target.result.split(',')[1];
                submitData.logoBase64 = base64;
                submitData.logoFileName = logoFile.name;
                onSubmit(submitData);
            };
            reader.readAsDataURL(logoFile);
        } else {
            onSubmit(submitData);
        }
    };

    return (
        <form id={FORM_COMPANY_EDIT_ID} onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
            {/* Logo upload */}
            <div>
                <label className="mb-[8px] block">
                    <span className="text-[14px] text-foreground">{t('account:logoDeLEntreprise')}</span>
                </label>
                <div className="flex items-start gap-[16px]">
                    <div className="h-[80px] w-[80px] rounded-[12px] bg-surface border-2 border-dashed border-border flex items-center justify-center overflow-hidden flex-shrink-0">
                        {logoPreview ? (
                            <img src={logoPreview} alt={t('account:logoPreview')} className="w-full h-full object-cover" />
                        ) : (
                            <Building2 className="h-8 w-8 text-muted-foreground" />
                        )}
                    </div>
                    <div className="flex-1">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/jpg"
                            className="hidden"
                            onChange={handleLogoSelect}
                        />
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                className="flex items-center gap-[8px] px-[16px] py-[10px] bg-foreground rounded-[10px] hover:bg-foreground/90 transition-colors"
                            >
                                <Camera className="h-4 w-4 text-background" />
                                <span className="text-[13px] text-background">
                                    {logoPreview ? t('account:changerLeLogo') : t('account:ajouterUnLogo')}
                                </span>
                            </button>
                            {logoFile && (
                                <button
                                    type="button"
                                    onClick={handleLogoRemove}
                                    className="flex items-center justify-center h-[40px] w-[40px] rounded-[10px] border border-border hover:bg-surface-hover transition-colors"
                                >
                                    <X className="h-4 w-4 text-muted-foreground" />
                                </button>
                            )}
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-[8px]">
                            {t('account:formatRecommandePngOuJpg200x200pxMinimum')}
                        </p>
                    </div>
                </div>
            </div>

            <Field>
                <FieldLabel required>{t('account:nomDeLEntreprise')}</FieldLabel>
                <Input
                    type="text"
                    placeholder={t('account:monEntrepriseSas')}
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    required
                />
            </Field>

            <Field>
                <FieldLabel required>SIRET</FieldLabel>
                <Input
                    type="text"
                    placeholder="123 456 789 00012"
                    maxLength={14}
                    value={formData.siret}
                    onChange={(e) => handleChange('siret', e.target.value)}
                    required
                />
            </Field>

            <Field>
                <FieldLabel>{t('account:nTvaIntracommunautaire')}</FieldLabel>
                <Input
                    type="text"
                    placeholder={t('account:fr12345678901')}
                    maxLength={20}
                    value={formData.vat}
                    onChange={(e) => handleChange('vat', e.target.value)}
                />
            </Field>

            <Field>
                <FieldLabel>{t('account:description')}</FieldLabel>
                <Textarea
                    placeholder={t('account:descriptionDeLEntreprise')}
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    rows={3}
                />
            </Field>

            <Field>
                <FieldLabel>{t('account:notes')}</FieldLabel>
                <Textarea
                    placeholder={t('account:notesInternes')}
                    value={formData.notes}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    rows={2}
                />
            </Field>

            <Field>
                <FieldLabel required>{t('account:adresse')}</FieldLabel>
                <Input
                    type="text"
                    placeholder={t('account:15AvenueDeLaRepublique')}
                    value={formData.street}
                    onChange={(e) => handleChange('street', e.target.value)}
                    required
                />
            </Field>

            <div className="grid grid-cols-2 gap-2">
                <Field>
                    <FieldLabel required>{t('account:codePostal')}</FieldLabel>
                    <Input
                        type="text"
                        placeholder="69003"
                        value={formData.postalCode}
                        onChange={(e) => handleChange('postalCode', e.target.value)}
                        required
                    />
                </Field>
                <Field>
                    <FieldLabel required>{t('account:ville')}</FieldLabel>
                    <Input
                        type="text"
                        placeholder={t('account:lyon')}
                        value={formData.city}
                        onChange={(e) => handleChange('city', e.target.value)}
                        required
                    />
                </Field>
            </div>

            <Field>
                <FieldLabel required>{t('account:emailDeContact')}</FieldLabel>
                <Input
                    type="email"
                    placeholder="contact@monentreprise.fr"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    required
                />
            </Field>

            <Field>
                <FieldLabel>{t('account:emailDeFacturation')}</FieldLabel>
                <Input
                    type="email"
                    placeholder="facturation@monentreprise.fr"
                    value={formData.billingEmail}
                    onChange={(e) => handleChange('billingEmail', e.target.value)}
                />
            </Field>

            <Field>
                <FieldLabel>{t('account:telephone')}</FieldLabel>
                <Input
                    type="tel"
                    placeholder="+33 4 72 00 00 00"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                />
            </Field>
            { user?.role === 'admin' && (
                <Field>
                    <FieldLabel required>
                        {t('account:identifiantDeFacturation')}
                    </FieldLabel>
                    <Input
                        type="text"
                        placeholder={t('account:identifiantDeFacturation')}
                        value={formData.billingIdentifier}
                        onChange={(e) => handleChange('billingIdentifier', e.target.value)}
                        required
                    />
                </Field>
            )
            }
        </form>
    );
}
